import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { CONTACT } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ id, label, type = "text", value, onChange, required, textarea }) => (
  <div className="relative">
    <label htmlFor={id} className="mb-2 block font-mono text-[11px] tracking-widest uppercase text-white/40">
      {label} {required && <span className="text-[#F59E0B]">*</span>}
    </label>
    {textarea ? (
      <textarea
        id={id}
        data-testid={`contact-${id}`}
        value={value}
        onChange={onChange}
        required={required}
        rows={4}
        className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white placeholder-white/20 outline-none transition-all duration-300 focus:border-[#3B82F6]/60 focus:bg-white/[0.05] focus:shadow-[0_0_24px_rgba(59,130,246,0.2)]"
      />
    ) : (
      <input
        id={id}
        type={type}
        data-testid={`contact-${id}`}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white placeholder-white/20 outline-none transition-all duration-300 focus:border-[#3B82F6]/60 focus:bg-white/[0.05] focus:shadow-[0_0_24px_rgba(59,130,246,0.2)]"
      />
    )}
  </div>
);

export const ContactForm = () => {
  const { t } = useLang();
  const f = CONTACT.fields;
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t(f.success));
      setForm({ name: "", email: "", company: "", role: "", interest: "", message: "" });
    } catch (err) {
      toast.error(t(f.error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-white/[0.07] bg-[#0A0E17]/80 backdrop-blur p-6 md:p-10">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="name" label={t(f.name)} value={form.name} onChange={set("name")} required />
        <Field id="email" label={t(f.email)} type="email" value={form.email} onChange={set("email")} required />
        <Field id="company" label={t(f.company)} value={form.company} onChange={set("company")} />
        <Field id="role" label={t(f.role)} value={form.role} onChange={set("role")} />
      </div>
      <div className="mt-5">
        <Field id="interest" label={t(f.interest)} value={form.interest} onChange={set("interest")} />
      </div>
      <div className="mt-5">
        <Field id="message" label={t(f.message)} value={form.message} onChange={set("message")} required textarea />
      </div>
      <motion.button
        type="submit"
        disabled={loading}
        whileTap={{ scale: 0.98 }}
        data-testid="contact-submit-button"
        className="mt-7 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#3B82F6] px-8 py-4 text-sm font-medium text-white transition-all duration-300 shadow-[0_0_30px_rgba(59,130,246,0.4)] hover:bg-[#60A5FA] hover:shadow-[0_0_45px_rgba(59,130,246,0.65)] disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? t(f.sending) : t(f.submit)}
      </motion.button>
    </form>
  );
};
